import React, { useEffect, useState } from 'react';
import PropertyCard from './PropertyCard';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getViewedListingIds } from '../lib/viewHistory';
import { buildTasteProfile } from '../lib/recentlyViewed';
import { pickSimilarListings } from '../lib/similarMatching';
import { computeQualityScore } from '../lib/listingQuality';
import './RecentlyViewedSimilar.css';

const TARGET = 4;
// Bounded candidate pool — NOT a full-table select. Sized to give the
// tiered similar-matcher enough rows to find a city/type/price match while
// keeping the payload small.
const CANDIDATE_POOL_LIMIT = 60;

// Only the fields buildTasteProfile actually reads.
const VIEWED_FIELDS = 'id, city, type, price, beds';

// Mirrors the FeaturedListings carousel field list (which is known good
// against the live schema) plus user_id for the owner-exclusion filter.
const CANDIDATE_FIELDS = [
  'id', 'title', 'price', 'price_label', 'type',
  'beds', 'baths', 'sqm', 'city', 'area',
  'images', 'description', 'address', 'plot_size',
  'contact_phone', 'contact_email', 'status',
  'is_boosted', 'boost_expires_at', 'is_featured',
  'user_id', 'created_at',
].join(', ');

// Translate a listings row into the camelCase shape PropertyCard +
// pickSimilarListings expect. Same mapping as the carousel uses.
function mapRow(row) {
  const listing = {
    id:               row.id,
    title:            row.title,
    price:            row.price,
    priceType:        row.price_label,
    type:             row.type,
    bedrooms:         row.beds,
    bathrooms:        row.baths,
    size:             row.sqm,
    city:             row.city,
    region:           row.area,
    address:          row.address,
    plotSize:         row.plot_size,
    description:      row.description,
    images:           row.images || [],
    image:            row.images?.[0] || null,
    contact_phone:    row.contact_phone,
    contact_email:    row.contact_email,
    status:           row.status || 'active',
    is_boosted:       row.is_boosted || false,
    boost_expires_at: row.boost_expires_at || null,
    isFeatured:       row.is_featured,
    user_id:          row.user_id,
    created_at:       row.created_at || null,
    gradient:         '#F2F1ED',
  };
  listing.qualityScore = computeQualityScore(listing);
  return listing;
}

export default function RecentlyViewedSimilar() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const viewedIds = await getViewedListingIds(user);
      if (cancelled) return;

      if (!viewedIds.length) {
        setPicks([]);
        setLoading(false);
        return;
      }

      // Hydrate the viewed listings — only the columns needed to build the
      // taste profile. Targeted via .in('id', viewedIds), max 10 rows.
      const { data: viewedRows, error: viewedErr } = await supabase
        .from('listings')
        .select(VIEWED_FIELDS)
        .in('id', viewedIds);
      if (cancelled) return;
      if (viewedErr) {
        setPicks([]);
        setLoading(false);
        return;
      }

      const viewedListings = (viewedRows || []).map(r => ({
        id:       r.id,
        city:    r.city,
        type:    r.type,
        price:   typeof r.price === 'number' ? r.price : 0,
        bedrooms: r.beds ?? 0,
      }));

      const profile = buildTasteProfile(viewedListings);
      if (!profile) {
        setPicks([]);
        setLoading(false);
        return;
      }

      // Candidate pool — bounded fetch of fresh, non-archived listings. Not
      // a full-table select.
      const { data: poolRows, error: poolErr } = await supabase
        .from('listings')
        .select(CANDIDATE_FIELDS)
        .not('status', 'in', '(archived,deleted)')
        .order('created_at', { ascending: false })
        .limit(CANDIDATE_POOL_LIMIT);
      if (cancelled) return;
      if (poolErr) {
        setPicks([]);
        setLoading(false);
        return;
      }

      let pool = (poolRows || []).map(mapRow);
      // Exclude the user's own listings from suggestions.
      if (user?.id) {
        pool = pool.filter(l => l.user_id !== user.id);
      }

      const result = pickSimilarListings(profile, pool, {
        target:     TARGET,
        excludeIds: viewedIds,
      });

      if (cancelled) return;
      setPicks(result);
      setLoading(false);
    }

    setLoading(true);
    load();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Render null while loading, when no history, or when no matches.
  if (loading || picks.length === 0) return null;

  return (
    <section className="homepage__viewedSim">
      <div className="homepage__viewedSimInner">
        <div className="homepage__viewedSimHeader">
          <div className="homepage__viewedSimEyebrow">
            <span className="homepage__viewedSimEyebrowLine" />
            <span className="homepage__viewedSimEyebrowText">
              {t('home.recentlyViewed.eyebrow')}
            </span>
            <span className="homepage__viewedSimEyebrowLine" />
          </div>
          <p className="homepage__viewedSimSubtitle">
            {t('home.recentlyViewed.subtitle')}
          </p>
        </div>
        <div className="homepage__viewedSimGrid">
          {picks.map((l, i) => (
            <PropertyCard key={l.id} listing={l} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
